import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LotteryInit } from '../../shared/models/LotteryInit.interface';
import { LotteryRegister } from '../../shared/models/LotteryRegister.interface';
import { LotteryPage } from '../../shared/models/LotteryPage.interface';
import { LotteryParticipantInvite } from '../models/LotteryParticipantInvite.interface';
import { LotteryPageEdit } from '../models/LotteryPageEdit.interface';
import { LotteryParticipantAdd } from '../models/LotteryParticipantAdd.interface';
import { LotteryParticipantRemove } from '../models/LotteryParticipantRemove.interface';
import { LotteryReferenceData } from '../models/LotteryReferenceData.interface';
import { LotteryMessages } from '../models/LotteryMessages.interface';
import { LotteryMessagePost } from '../models/LotteryMessagePost.interface';
import { environment } from '../../../environments/environment';
import { makeStateKey, TransferState } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable({
  providedIn: 'root'
})
export class DrawNamesService {
  private baseUrl = environment.apiUrl;
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);
  emailGiver = '';
  codeConfirmation: string;

  constructor(private httpClient: HttpClient) {}

  postParticipantInvite(participantInv: LotteryParticipantInvite[]) {
    const body = JSON.stringify(participantInv);
    return this.httpClient.post(this.baseUrl + '/Drawnames/ParticipantInvite', body, httpOptions);
  }

  postStart(start: LotteryInit) {
    const body = JSON.stringify(start);
    return this.httpClient.post(this.baseUrl + '/Drawnames/start?errEmailSuggest=true', body, httpOptions);
  }

  postLotteryEventInactive(participantKey: string) {
    const encodedParticipantKey = encodeURIComponent(participantKey);
    return this.httpClient.post(`${this.baseUrl}/Drawnames/lotteryEventInactive?participantKey=${encodedParticipantKey}`, httpOptions);
  }

  getLotteryPage(participantKey: string): Observable<LotteryPage> {
    const encodedParticipantKey = encodeURIComponent(participantKey);
    const stateKey = makeStateKey<LotteryPage>(`lottery-page-${encodedParticipantKey}`);

    // Check if we have data in transfer state (client-side)
    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        // Remove the stored data to prevent memory leaks
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    // Make the API call and store result in transfer state if on server
    return this.httpClient
      .get<LotteryPage>(`${this.baseUrl}/Drawnames/lotteryPage?participantKey=${encodedParticipantKey}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  getLostLink(email: string): Observable<LotteryPage> {
    const encodedEmail = encodeURIComponent(email);
    const stateKey = makeStateKey<LotteryPage>(`lost-link-${encodedEmail}`);

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get<LotteryPage>(`${this.baseUrl}/Drawnames/lostLink?email=${encodedEmail}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  getLinkMailLastYearEvent(email: string): Observable<unknown> {
    const encodedEmail = encodeURIComponent(email);
    const stateKey = makeStateKey<unknown>(`last-year-${encodedEmail}`);

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get(`${this.baseUrl}/Drawnames/linkMailLastYearEvent?email=${encodedEmail}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  getLotteryReferenceData(participantKey: string): Observable<LotteryReferenceData[]> {
    const encodedParticipantKey = encodeURIComponent(participantKey);
    const stateKey = makeStateKey<LotteryReferenceData[]>(`reference-data-${encodedParticipantKey}`);

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get<LotteryReferenceData[]>(`${this.baseUrl}/Drawnames/lotteryReferenceData?participantKey=${encodedParticipantKey}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  getLotteryRegister(lotteryKey: string): Observable<LotteryRegister> {
    const encodedLotteryKey = encodeURIComponent(lotteryKey);
    const stateKey = makeStateKey<LotteryRegister>(`lottery-register-${encodedLotteryKey}`);

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get<LotteryRegister>(`${this.baseUrl}/Drawnames/lotteryRegister?lotteryKey=${encodedLotteryKey}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  postLotteryMessage(lotteryMessagePost: LotteryMessagePost) {
    const body = JSON.stringify(lotteryMessagePost);
    return this.httpClient.post(this.baseUrl + '/Drawnames/postMessage', body, httpOptions);
  }

  getLotteryMessages(participantKey: string): Observable<LotteryMessages> {
    const encodedParticipantKey = encodeURIComponent(participantKey);
    const stateKey = makeStateKey<LotteryMessages>(`lottery-messages-${encodedParticipantKey}`);

    if (isPlatformBrowser(this.platformId)) {
      const storedData = this.transferState.get(stateKey, null);
      if (storedData) {
        this.transferState.remove(stateKey);
        return of(storedData);
      }
    }

    return this.httpClient
      .get<LotteryMessages>(`${this.baseUrl}/Drawnames/getMessages?participantKey=${encodedParticipantKey}`)
      .pipe(
        tap(response => {
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, response);
          }
        })
      );
  }

  postLotteryPageEdit(lotteryPageEdit: LotteryPageEdit) {
    const body = JSON.stringify(lotteryPageEdit);
    return this.httpClient.post(this.baseUrl + '/Drawnames/lotteryPageEdit', body, httpOptions);
  }

  postLotteryAddParticipant(lotteryParticipantAdd: LotteryParticipantAdd) {
    const body = JSON.stringify(lotteryParticipantAdd);
    return this.httpClient.post(this.baseUrl + '/Drawnames/addParticipant', body, httpOptions);
  }

  postLotteryRemoveParticipant(lotteryParticipantRemove: LotteryParticipantRemove) {
    const body = JSON.stringify(lotteryParticipantRemove);
    return this.httpClient.post(this.baseUrl + '/Drawnames/removeParticipant', body, httpOptions);
  }

  postUpdateParticipant(lotteryKey: string, name: string, email: string) {
    const encodedLotteryKey = encodeURIComponent(lotteryKey);
    const encodedName = encodeURIComponent(name);
    const encodedEmail = encodeURIComponent(email);

    const url = `${this.baseUrl}/Drawnames/updateParticipant?lotteryKey=${encodedLotteryKey}&name=${encodedName}&email=${encodedEmail}&errEmailSuggest=true`;

    return this.httpClient.post(url, httpOptions);
  }

  postAskWishList(participantKey: string) {
    const encodedParticipantKey = encodeURIComponent(participantKey);
    return this.httpClient.post(`${this.baseUrl}/Drawnames/askWishList?participantKey=${encodedParticipantKey}`, httpOptions);
  }
}